/**
 * Analytics Service for User Tier Management
 * 
 * Provides API calls for:
 * - Usage analytics and statistics
 * - Cost breakdown by model/provider
 * - Quota status and remaining limits
 * - Chart data for visualization
 * 
 * Implements Requirements: 5.1, 5.3, 5.4, 5.5
 */

import config from '../config/api';
import logger from '../logging/logger';

// Types for analytics data
export interface UsageAnalytics {
  user_id: string;
  time_window: string;
  total_tokens: number;
  total_requests: number;
  total_cost: number;
  input_tokens: number;
  output_tokens: number;
  usage_by_model: Record<string, number>;
  usage_by_agent: Record<string, number>;
  usage_by_provider: Record<string, number>;
  usage_by_hour?: Record<number, number>;
  usage_by_day?: Record<string, number>;
  remaining_quota?: Record<string, number>;
  quota_utilization?: Record<string, number>;
}

export interface CostBreakdown {
  total_cost: number;
  by_model: Record<string, number>;
  by_provider: Record<string, number>;
  by_agent: Record<string, number>;
  by_day: Record<string, number>;
  input_cost: number;
  output_cost: number;
}

export interface QuotaStatus {
  user_id: string;
  tier: string;
  developer_mode: boolean;
  has_custom_limits: boolean;
  quota: {
    remaining: Record<string, number | string>;
    utilization: Record<string, number>;
    current_usage: Record<string, number>;
    current_requests: Record<string, number>;
    limits: {
      tokens: Record<string, number | string>;
      requests: Record<string, number | string>;
    };
    developer_mode: boolean;
    status?: string;
  };
  reset_times: {
    minute: string;
    hour: string;
    day: string;
    month: string;
    seconds_until_minute_reset: number;
    seconds_until_hour_reset: number;
    seconds_until_day_reset: number;
    seconds_until_month_reset: number;
  };
  warnings: Array<{
    level: 'warning' | 'critical';
    message: string;
    quota_type: string;
  }>;
}

export interface ChartDataset {
  label: string;
  data: number[];
  borderColor?: string;
  backgroundColor?: string | string[];
  fill?: boolean;
  borderWidth?: number;
}

export interface ChartData {
  chart_type: string;
  title: string;
  x_axis_label: string;
  y_axis_label: string;
  labels: string[];
  datasets: ChartDataset[];
  time_range: {
    start: string;
    end: string;
  };
  total_data_points: number;
}

export interface TierInfo {
  name: string;
  tokens_per_minute: number;
  tokens_per_hour: number;
  tokens_per_day: number;
  tokens_per_month: number;
  requests_per_minute: number;
  requests_per_hour: number;
  requests_per_day: number;
  requests_per_month: number;
  cost_per_month: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class QuotaAnalyticsService {
  private baseUrl: string;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private cacheTTL = 5000; // 5 seconds - prevents duplicate calls from re-renders

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  private getCached<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < this.cacheTTL) {
      return entry.data as T;
    }
    return null;
  }

  private setCache<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Get usage analytics for a user
   * Implements Requirement 5.2: Show current usage statistics
   */
  async getUsageAnalytics(
    userId: string,
    token: string,
    timeWindow: string = 'day',
    includeQuota: boolean = true
  ): Promise<UsageAnalytics | null> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        time_window: timeWindow,
        include_quota: includeQuota.toString()
      });

      const response = await fetch(
        `${this.baseUrl}/analytics/usage?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch usage analytics:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching usage analytics:', error);
      return null;
    }
  }

  /**
   * Get cost breakdown for a user
   * Implements Requirements 5.4, 6.2, 6.3: Show estimated costs
   */
  async getCostBreakdown(
    userId: string,
    token: string,
    timePeriod: string = 'day'
  ): Promise<CostBreakdown | null> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        time_period: timePeriod
      });

      const response = await fetch(
        `${this.baseUrl}/analytics/cost?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch cost breakdown:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching cost breakdown:', error);
      return null;
    }
  }

  /**
   * Get quota status for a user
   * Implements Requirements 5.5, 5.6: Display remaining quota and developer mode status
   */
  async getQuotaStatus(
    userId: string,
    token: string
  ): Promise<QuotaStatus | null> {
    const cacheKey = `quota:${userId}`;
    const cached = this.getCached<QuotaStatus>(cacheKey);
    if (cached) return cached;

    try {
      const response = await fetch(
        `${this.baseUrl}/analytics/quota/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch quota status:', response.status);
        return null;
      }

      const data = await response.json();
      this.setCache(cacheKey, data);
      return data;
    } catch (error) {
      logger.error('Error fetching quota status:', error);
      return null;
    }
  }

  /**
   * Get chart data for usage visualization
   * Implements Requirement 5.3: Display usage data in interactive charts
   */
  async getUsageChartData(
    userId: string,
    token: string,
    timeRange: string = 'week',
    chartType: string = 'line'
  ): Promise<ChartData | null> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        time_range: timeRange,
        chart_type: chartType
      });

      const response = await fetch(
        `${this.baseUrl}/analytics/charts/usage?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch usage chart data:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching usage chart data:', error);
      return null;
    }
  }

  /**
   * Get chart data for cost visualization
   * Implements Requirement 5.4: Show estimated costs in charts
   */
  async getCostChartData(
    userId: string,
    token: string,
    timeRange: string = 'week',
    breakdownType: string = 'model'
  ): Promise<ChartData | null> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        time_range: timeRange,
        breakdown_type: breakdownType
      });

      const response = await fetch(
        `${this.baseUrl}/analytics/charts/cost?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch cost chart data:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching cost chart data:', error);
      return null;
    }
  }

  /**
   * Get user tier information
   */
  async getUserTierInfo(
    userId: string,
    token: string
  ): Promise<TierInfo | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/analytics/tier/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        logger.error('Failed to fetch tier info:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching tier info:', error);
      return null;
    }
  }

  /**
   * Format token count for display
   */
  formatTokens(tokens: number): string {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(2)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  }

  /**
   * Format cost for display
   */
  formatCost(cost: number): string {
    return `$${cost.toFixed(4)}`;
  }

  /**
   * Format percentage for display
   */
  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  /**
   * Get color based on utilization percentage
   */
  getUtilizationColor(percentage: number): string {
    if (percentage >= 90) return '#ef4444'; // Red - critical
    if (percentage >= 80) return '#f59e0b'; // Amber - warning
    if (percentage >= 60) return '#3b82f6'; // Blue - moderate
    return '#10b981'; // Green - healthy
  }

  /**
   * Format time until reset
   */
  formatTimeUntilReset(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}m`;
    } else if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    } else {
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      return `${days}d ${hours}h`;
    }
  }
}

export const quotaAnalyticsService = new QuotaAnalyticsService();
export default quotaAnalyticsService;
